import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const ACTIVE_COUPON_STORAGE_KEY = 'correctify-active-coupon';

@Injectable({
  providedIn: 'root'
})
export class CouponService {
  private platformId = inject(PLATFORM_ID);
  readonly activeCouponCode = signal<string | null>(this.loadInitialCouponCode());

  private normalizeCouponCode(rawValue: unknown): string | null {
    const normalized = String(rawValue ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '')
      .slice(0, 40);

    return normalized.length > 0 ? normalized : null;
  }

  private loadInitialCouponCode(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const storedCoupon = localStorage.getItem(ACTIVE_COUPON_STORAGE_KEY);
      return this.normalizeCouponCode(storedCoupon);
    } catch {
      return null;
    }
  }

  private persistCouponCode(code: string | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (code) {
        localStorage.setItem(ACTIVE_COUPON_STORAGE_KEY, code);
        return;
      }
      localStorage.removeItem(ACTIVE_COUPON_STORAGE_KEY);
    } catch {
      // no-op
    }
  }

  setActiveCouponCode(code: string | null): string | null {
    const normalized = this.normalizeCouponCode(code);
    this.activeCouponCode.set(normalized);
    this.persistCouponCode(normalized);
    return normalized;
  }

  clearActiveCouponCode(): void {
    this.setActiveCouponCode(null);
  }

  captureCouponFromUrl(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return this.activeCouponCode();
    }

    const url = new URL(window.location.href);
    const couponFromUrl = this.normalizeCouponCode(
      url.searchParams.get('cupom') ?? url.searchParams.get('coupon')
    );

    if (couponFromUrl) {
      this.setActiveCouponCode(couponFromUrl);
      url.searchParams.delete('cupom');
      url.searchParams.delete('coupon');
      const nextUrl = `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
      window.history.replaceState({}, document.title, nextUrl);
      return couponFromUrl;
    }

    return this.activeCouponCode();
  }
}
